import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import api from "../lib/api";
import CategoryIcon from "../components/CategoryIcon";

export default function Services() {
  const [categories, setCategories] = useState([]);
  const location = useLocation();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.hash && categories.length) {
      setTimeout(() => {
        document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
      }, 200);
    }
  }, [location.hash, categories]);

  return (
    <div className="bg-[#0B132B]" data-testid="services-page">
      <section className="py-16 lg:py-24 bg-[#0f1830] border-b border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono2 text-xs uppercase tracking-widest text-[#F97316] mb-3">Our Services</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-none" data-testid="services-page-title">
            50+ Services. One Partner.
          </h1>
          <p className="text-slate-400 mt-5 max-w-2xl text-sm sm:text-base leading-relaxed">
            Browse by category — every service includes flat quoted pricing, deadline tracking and a specialist who answers the phone.
          </p>
          <div className="flex flex-wrap gap-2 mt-8">
            {categories.map((c) => (
              <a key={c.id} href={`#${c.slug}`} data-testid={`services-anchor-${c.slug}`}
                className="text-xs font-medium border border-[#1E293B] bg-[#131C38] text-slate-300 hover:text-[#F97316] hover:border-[#F97316]/50 rounded-full px-4 py-2 transition-colors">
                {c.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-16">
        {categories.map((cat) => (
          <section key={cat.id} id={cat.slug} className="scroll-mt-24" data-testid={`category-section-${cat.slug}`}>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-11 h-11 rounded-md bg-[#F97316]/10 border border-[#F97316]/25 flex items-center justify-center text-[#F97316] shrink-0">
                <CategoryIcon icon={cat.icon} className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">{cat.name}</h2>
                <p className="text-xs text-slate-400 mt-0.5">{cat.description}</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(cat.services || []).map((s) => (
                <Link key={s.id} to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`}
                  className="group bg-[#131C38] border border-[#1E293B] rounded-lg p-5 hover:border-[#F97316]/60 hover:translate-y-[-2px] transition-all duration-300 flex flex-col">
                  <h3 className="font-semibold text-white text-sm mb-2 group-hover:text-[#F97316] transition-colors">{s.title}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed line-clamp-2 mb-4 flex-1">{s.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                      <Clock className="w-3 h-3" /> {s.turnaround}
                    </span>
                    <ArrowRight className="w-4 h-4 text-[#F97316] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
